import React, { useState, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import { testId } from 'ui-components/services/testid-service';
import { SelectedFieldListItem } from './selected-field-list-item';
import { useShiftKeyPress } from '../hooks';
import classnames from 'classnames';
import { AddFieldsMessage } from './add-fields-message';
import { AddFieldsErrorMessage } from './add-fields-error-message';
import style from '../export-records-modal.module.less';

export const SelectedFieldList = ({ selectedFields, selectedFieldsHaveError = false, editFieldName, setSelectedFields }) => {
  const shiftKeyPressed = useShiftKeyPress();
  const [lastClickedFieldId, setLastClickFieldId] = useState();
  const onDragEnd = useCallback(
    (result) => {
      const { source, destination, draggableId } = result;
      if (!destination) {
        return;
      }

      if (destination.droppableId === source.droppableId && destination.index === source.index) {
        return;
      }

      const newSelectedFields = [...selectedFields];
      newSelectedFields.splice(source.index, 1);

      const foundField = selectedFields.find((f) => f.fieldId === draggableId);
      const newField = {
        ...foundField,
      };
      newSelectedFields.splice(destination.index, 0, newField);
      setSelectedFields(newSelectedFields);
    },
    [selectedFields, setSelectedFields],
  );

  const shiftClickSelectedFields = useCallback(
    (fieldId) => {
      const currentSelectedFields = [...selectedFields];
      const clickedFieldIndex = currentSelectedFields.findIndex((f) => f.fieldId === fieldId);
      const lastClickedIndex = currentSelectedFields.findIndex((f) => f.fieldId === lastClickedFieldId);
      const [startIndex, endIndex] =
        lastClickedIndex < clickedFieldIndex ? [lastClickedIndex, clickedFieldIndex] : [clickedFieldIndex, lastClickedIndex];
      const updatedFields = currentSelectedFields.map((f, index) => {
        if (index !== lastClickedIndex && index >= startIndex && index <= endIndex) {
          return { ...f, wasClicked: !f.wasClicked };
        }
        return f;
      });
      setSelectedFields(updatedFields);
    },
    [selectedFields, lastClickedFieldId, setSelectedFields],
  );

  const onSelectedFieldItemClicked = useCallback(
    (fieldId) => {
      setLastClickFieldId(fieldId);
      if (shiftKeyPressed) {
        shiftClickSelectedFields(fieldId);
        return;
      }
      const updatedFields = selectedFields.map((f) => {
        if (f.fieldId === fieldId) {
          return {
            ...f,
            wasClicked: !f.wasClicked,
          };
        }
        return f;
      });
      setSelectedFields(updatedFields);
    },
    [selectedFields, shiftKeyPressed, shiftClickSelectedFields, setSelectedFields],
  );

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId='droppable-fields'>
        {(provided) => {
          return (
            <div className={style['selected-fields-wrap']} {...testId('selected-fields')}>
              {!selectedFieldsHaveError && <AddFieldsMessage />}
              {selectedFieldsHaveError && <AddFieldsErrorMessage />}
              <ul ref={provided.innerRef} className={selectedFieldsHaveError ? style['has-error'] : null} {...provided.droppableProps}>
                {selectedFields.map((field, index) => {
                  return (
                    <Draggable key={`${field.fieldId}.selected`} draggableId={field.fieldId} index={index}>
                      {(providedDrag) => {
                        return (
                          <div ref={providedDrag.innerRef} {...providedDrag.draggableProps} {...providedDrag.dragHandleProps}>
                            <SelectedFieldListItem
                              classNames={classnames(style['selected-field-item'], { [style.active]: !!field.wasClicked })}
                              editFieldName={editFieldName}
                              fieldId={field.fieldId}
                              fieldName={field.name}
                              modifiedFieldName={field.modifiedFieldName}
                              onClick={onSelectedFieldItemClicked}
                            />
                          </div>
                        );
                      }}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </ul>
            </div>
          );
        }}
      </Droppable>
    </DragDropContext>
  );
};

SelectedFieldList.propTypes = {
  selectedFields: PropTypes.array.isRequired,
  setSelectedFields: PropTypes.func.isRequired,
  selectedFieldsHaveError: PropTypes.bool,
  editFieldName: PropTypes.func.isRequired,
};
